import { runInThisContext } from "vm";
import { VOMedia, VOMediaType } from "../model/DataStructure.js";

export interface MediaContentOptions {
    container: HTMLElement;
    visible?: boolean;
}

export type MediaContentLayout = "landscape" | "portrait" | "intrinsic";

export abstract class MediaContent<Options extends MediaContentOptions = MediaContentOptions>{
    public onLoad = e5.Signal.create();
    public onReady = e5.Signal.create();
    public onHide = e5.Signal.create();
    public onShow = e5.Signal.create();

    public abstract readonly type: VOMediaType;

    protected _element: HTMLDivElement;
    protected _body: HTMLDivElement;
    protected _footer: HTMLDivElement;
    protected _caption: HTMLDivElement;
    protected _control: HTMLDivElement;
    protected _options: Options;

    constructor(options: Options) {
        options = e5.object.merge<MediaContentOptions>({
            visible: false
        }, options) as Options;
        this._options = options;

        this._element = document.createElement("div");
        this._element.className = "media-content";
        this._element.innerHTML = `
        <div class="media-content__body"></div>
        <div class="media-content__footer">
            <div class="media-content__caption e5-hidden"></div>
            <div class="media-content__control"></div>
        </div>
        `;

        this._body = this._element.querySelector(".media-content__body");
        this._footer = this._element.querySelector(".media-content__footer");
        this._caption = this._element.querySelector(".media-content__caption");
        this._control = this._element.querySelector(".media-content__control");

        this.onLoad.add(() => {
            this._element.classList.toggle("media-content--ready", false);
        });

        this.onReady.add(() => {
            this._element.classList.toggle("media-content--ready", true);
        });

        this.visible = options.visible;

        options.container.appendChild(this._element);
    }

    protected getLayout(aspectRatio:number):MediaContentLayout{
        if (aspectRatio >= (16 / 9)) {
            return "intrinsic";
        }
        return aspectRatio > 1 ? "landscape" : "portrait";
    }

    public prepare(data: VOMedia): void {
        let aspectRatio = this.getAspectRatio(data);
        if (aspectRatio === null) {
            aspectRatio = 16 / 9;
        }
        this._element.setAttribute("data-layout", this.getLayout(aspectRatio));
    }

    protected getAspectRatio(data: VOMedia): number | null {
        return null;
    }

    public abstract load(data: VOMedia): void;

    public get visible(): boolean {
        return !this._element.classList.contains("media-content--hidden");
    }

    public set visible(value: boolean) {
        if(this.visible === value){
            return;
        }
        this._element.classList.toggle("media-content--hidden", !value);
        if(value){
            this.onShow.dispatch();
        } else{
            this.onHide.dispatch();
        }
    }

    protected get caption(): string {
        return this._caption.textContent;
    }

    protected set caption(value: string) {
        this._caption.textContent = value;
        this._caption.classList.toggle("e5-hidden", !value);
    }
}